import { type PurchaseOrderDTO } from "@finway-group/shared/lib/models"
import { QueryParamsFilter } from "@finway-group/shared/lib/models/queryParams/queryParams.types"
import { TablePaginationConfig } from "antd/lib/table"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSetQueryParamsStorage } from "~Features/pages/deliveryNotes/hooks/usePaginationStorage.hook"
import { queryClient } from "~Shared/config/reactQueryConfig"
import useLocalStorage from "~Shared/hooks/useLocalStorage"
import { GENERAL_QUERY_STALE_TIME, QueryParams } from "~Shared/queries/consts"
import { defaultQueryParams } from "~Shared/queries/vendors/consts"
import { NotificationService } from "~Shared/services"
import DialogService from "~Shared/services/dialog.service"
import { PURCHASE_ORDERS_QUERY_PARAMS, PURCHASE_ORDERS_TABLE_LIMIT } from "~Shared/services/localstorage.service"
import { getCurrentPageInfo } from "~Shared/utils/dashboard.utils"
import {
    PURCHASE_ORDER_QUERY_KEYS,
    useCreateAndSubmitPurchaseOrderMutation,
    useCreatePurchaseOrderMutation,
    useDeletePurchaseOrderMutation,
    usePurchaseOrdersQuery,
    useTransitionPurchaseOrderMutation,
    useUpdatePurchaseOrderMutation,
} from "../queries/purchaseOrder.queries"
import { PurchaseOrderApiService } from "../service/purchaseOrderApi.service"
import { type PurchaseOrder } from "../types/PurchaseOrder.interfaces"
import { mapPurchaseOrderToTable } from "../types/utils/helper.util"

export const usePurchaseOrder = () => {
    const { t } = useTranslation()
    const getPO = async (poId: string) => {
        try {
            const result = await queryClient.fetchQuery<PurchaseOrder>({
                queryKey: PURCHASE_ORDER_QUERY_KEYS.DETAIL(poId),
                queryFn: () => PurchaseOrderApiService.getPurchaseOrderById(poId),
                staleTime: GENERAL_QUERY_STALE_TIME,
            })

            return result
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t(`error:error`))
            return undefined
        }
    }

    return {
        getPO,
    }
}

export function usePurchaseOrders(filter: QueryParamsFilter = {}) {
    const [search, setSearch] = useState("")
    const [currentPage, setCurrentPage] = useState(defaultQueryParams.page)
    const [limit, setLimit] = useLocalStorage(PURCHASE_ORDERS_TABLE_LIMIT, defaultQueryParams.limit)

    const [sortOption, setSortOption] = useState<Required<QueryParams>["sortOption"]>(defaultQueryParams.sortOption)
    const [sortField, setSortField] = useState<Required<QueryParams>["sortField"]>(defaultQueryParams.sortField)
    const sortParams = useMemo(() => [{ sortField, sortOption }], [sortField, sortOption])
    useSetQueryParamsStorage({ page: currentPage, limit, search, sortParams }, PURCHASE_ORDERS_QUERY_PARAMS)

    const {
        data: serverPOs,
        isLoading,
        isError,
        error,
        refetch,
    } = usePurchaseOrdersQuery({
        page: currentPage,
        limit,
        search,
        filter,
        sortParams,
    })

    const tableData = useMemo(() => (serverPOs ? serverPOs.docs.map(mapPurchaseOrderToTable) : []), [serverPOs])

    const handleSearch = useCallback(
        (value: string) => {
            setSearch(value)
            setCurrentPage(defaultQueryParams.page)
        },
        [setSearch],
    )

    // On the dashboard (Procurement tab), we ignore filters for deciding whether to show the "empty table" or "no search data found" message, because:
    // a. That's also how all the other dashboard tabs behave.
    // b. There's ALWAYS an invisible filter by type "PROCUREMENT"
    const { isOnExpensePage } = getCurrentPageInfo("")
    const isFilterApplied = Object.keys(filter).length > 0
    const isNoSearchDataFound = tableData?.length === 0 && (!!search || (isFilterApplied && !isOnExpensePage))
    const isTableEmpty = tableData?.length === 0 && !search && (!isFilterApplied || isOnExpensePage)

    const pagination: Pick<Required<TablePaginationConfig>, "total" | "current" | "pageSize"> = {
        total: serverPOs?.totalDocs || 0,
        current: currentPage,
        pageSize: limit,
    }
    return {
        tableData,
        serverData: serverPOs?.docs ?? [],
        isLoading,
        isError,
        error,
        refetch,
        handleSearch,
        search,
        isNoSearchDataFound,
        isTableEmpty,
        pagination,
        setCurrentPage,
        setPageSize: setLimit,
        setSortField,
        setSortOption,
        resetSorting: () => {
            setSortField(defaultQueryParams.sortField)
            setSortOption(defaultQueryParams.sortOption)
        },
    }
}

export function useMutatePurchaseOrders() {
    // Mutations
    const createPurchaseOrderMutation = useCreatePurchaseOrderMutation()
    const transitionPurchaseOrderMutation = useTransitionPurchaseOrderMutation()
    const updatePurchaseOrderMutation = useUpdatePurchaseOrderMutation()
    const deletePurchaseOrderMutation = useDeletePurchaseOrderMutation()
    const createAndSubmitPurchaseOrderMutation = useCreateAndSubmitPurchaseOrderMutation()

    const createPO = useCallback(
        (po: PurchaseOrderDTO, onSuccess?: () => void) => {
            createPurchaseOrderMutation.mutate(po, { onSuccess })
        },
        [createPurchaseOrderMutation],
    )

    const transitionPO = useCallback(
        (id: string, po: { action: string }, onSuccess?: () => void) => {
            transitionPurchaseOrderMutation.mutate({ id, transition: { action: po.action } }, { onSuccess })
        },
        [transitionPurchaseOrderMutation],
    )

    const createAndSubmitPO = useCallback(
        (po: PurchaseOrderDTO, onSuccess?: () => void) => {
            createAndSubmitPurchaseOrderMutation.mutate(po, {
                onSuccess,
            })
        },
        [createAndSubmitPurchaseOrderMutation],
    )

    const updatePO = useCallback(
        async (id: string, po: PurchaseOrderDTO, onSuccess?: () => void) => {
            await updatePurchaseOrderMutation.mutateAsync({ id, po }, { onSuccess })
        },
        [updatePurchaseOrderMutation],
    )

    const deletePo = useCallback(
        async (id: string, onSuccess?: () => void) => {
            const isConfirmed = await DialogService.confirmPurchaseOrderDeletion()
            if (isConfirmed) {
                deletePurchaseOrderMutation.mutate(id, { onSuccess })
            }
        },
        [deletePurchaseOrderMutation],
    )

    const createPurchaseOrderLoading = createPurchaseOrderMutation.isLoading
    const transitionPurchaseOrderLoading = transitionPurchaseOrderMutation.isLoading
    const updatePurchaseOrderLoading = updatePurchaseOrderMutation.isLoading
    const createAndSubmitPurchaseOrderLoading = createAndSubmitPurchaseOrderMutation.isLoading

    return {
        createPO,
        createPurchaseOrderLoading,
        transitionPO,
        transitionPurchaseOrderLoading,
        createAndSubmitPO,
        createAndSubmitPurchaseOrderLoading,
        updatePO,
        updatePurchaseOrderLoading,
        deletePo,
    }
}
